import type { Tunables } from '../sim/tunables';
import { el } from './dom';

export type GuideStep = 'call' | 'throw' | 'catch' | 'run';

interface GuideState {
  call?: boolean;
  throw?: boolean;
  catch?: boolean;
  run?: number;
}

/**
 * The guided first drive (GDD 6.10): one coach line the first time each thing comes up (pick a play,
 * throw, time the catch, steer the run). What has been shown is remembered on this device, so a
 * returning player is not taught twice. ?guide in the URL starts it again.
 */
export class Guide {
  readonly root = el('div', 'coach');
  private readonly head = el('div', 'coach-head');
  private readonly sub = el('div', 'coach-sub');
  private state: GuideState = {};

  constructor(
    private readonly t: Tunables,
    reset: boolean,
  ) {
    this.root.append(this.head, this.sub);
    this.root.hidden = true;
    this.root.dataset.testid = 'coach';
    if (reset) this.save();
    else this.state = this.load();
  }

  /** True while this step still needs teaching. */
  need(step: GuideStep): boolean {
    if (step === 'run') return (this.state.run ?? 0) < this.t.guide.runHintCarries;
    return !this.state[step];
  }

  done(step: GuideStep): void {
    if (step === 'run') this.state.run = (this.state.run ?? 0) + 1;
    else this.state[step] = true;
    this.save();
  }

  say(head: string, sub: string, where: 'top' | 'middle' = 'middle'): void {
    this.head.textContent = head;
    this.sub.textContent = sub;
    this.root.dataset.where = where;
    this.root.hidden = false;
    this.root.classList.remove('in');
    void this.root.offsetWidth;
    this.root.classList.add('in');
  }

  hide(): void {
    this.root.hidden = true;
  }

  private load(): GuideState {
    try {
      const raw = localStorage.getItem(this.t.guide.storageKey);
      return raw ? (JSON.parse(raw) as GuideState) : {};
    } catch {
      return {};
    }
  }

  private save(): void {
    try {
      localStorage.setItem(this.t.guide.storageKey, JSON.stringify(this.state));
    } catch {
      // Private mode or storage blocked: the guide just shows again next visit.
    }
  }
}
